// Key the pricing engine's per-item signals by the item's UUID (quote_item_id) —
// never by row position or the stored item_index.
//
// The diagnostics feed (GET /api/deals/:id/diagnostics) now attaches quote_item_id to
// each item, resolved server-side from the deal's live items (deals.js). So an item
// that wasn't priced — leaving a gap in the stored-index sequence — or a reordered
// quote can never shift one item's signals onto another's line: an id either matches
// its own row or matches nothing. This is the same identity the categories, comps and
// every per-item write already travel by.
//
// All five columns are confirmed present in `deal_diagnostics` (diagnostics.js). Note
// `supply_depth` is NOT — it lives only in fact_pricing — so the supply/liquidity read
// rides `market_state` (A–E regime) instead.
const SIGNAL_FIELDS = ['demand_tier', 'sale_probability', 'market_state', 'viable', 'risk_flags'];

// Returns an array aligned to `spineItems`: each entry that item's signals object, or
// `null` when the diagnostics feed has nothing for that quote_item_id (unpriced item,
// parked quote, or a row whose signals are all blank).
export function mergeItemSignals(spineItems, diagnosticsItems) {
    const items = spineItems || [];
    const diag = diagnosticsItems || [];

    const byId = new Map();
    for (const entry of diag) {
        const id = entry?.quote_item_id;
        const summary = entry?.summary;
        if (!id || !summary) continue;
        const signals = {};
        for (const field of SIGNAL_FIELDS) signals[field] = summary[field] ?? null;
        // A parked/unpriced row carries an all-null summary — no chips for it. The
        // suppression check is on the categorical signals only; volatility_pct and the
        // identity read (identity_status/ic_score) ride along on a row that already has
        // signals — they must not by themselves keep an otherwise-empty row alive.
        if (Object.values(signals).some(v => v != null)) {
            signals.volatility_pct = summary.volatility_pct ?? null;
            signals.identity_status = summary.identity_status ?? null;
            signals.ic_score = summary.ic_score ?? null;
            byId.set(id, signals);
        }
    }

    return items.map(it => (it?.quote_item_id ? byId.get(it.quote_item_id) ?? null : null));
}

// The engine's own SP/BP per item, in pounds, aligned to `spineItems` the same
// way. On a price-agreed quote Prism stores the agreed price, so this is the
// only place the engine's view still shows (spec docs/specs_20261021_price-agreed-holds.md).
export function engineFigures(spineItems, diagnosticsItems) {
    const byId = new Map();
    for (const entry of diagnosticsItems || []) {
        const { final_sp: sp, final_bp: bp } = entry?.summary || {};
        if (entry?.quote_item_id && (sp != null || bp != null)) byId.set(entry.quote_item_id, { sp, bp });
    }
    return (spineItems || []).map(it => byId.get(it?.quote_item_id) ?? null);
}
